import { Input, Select, Typography } from '@smartgift/frontend-toolkit';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Card } from '@hero/atoms';
import { DetailItem } from '@hero/components/molecules';
import {
  MAX_ADDRESS_LENGTH,
  MAX_CITY_NAME_LENGTH,
  MAX_COMPANY_NAME_LENGTH,
  MAX_ZIP_CODE_LENGTH,
  US_STATE_LIST
} from '@hero/constants';
import { useIsAdminOrganization } from '@hero/hooks';
import { useUpdateCompany } from '@hero/services/organization-service/hooks';

import * as S from './style';
import { companyProfileSchema } from './validationSchema';

function CompanyProfileForm({ organization }) {
  const { t } = useTranslation();
  const { mutateAsync: onCompanyUpdate } = useUpdateCompany();
  const isOrganizationOwner = useIsAdminOrganization();

  const formik = useFormik({
    initialValues: {
      name: organization?.name ?? '',
      companySize: organization?.companySize
        ? {
            value: organization.companySize,
            label: organization.companySize === '1-25' ? t('lessThan25') : organization.companySize
          }
        : null,
      department: organization?.department
        ? {
            value: organization.department,
            label: organization.department
          }
        : null,
      billingAddress: {
        addressLine1: organization?.billingAddress?.addressLine1 ?? '',
        city: organization?.billingAddress?.city ?? '',
        state: organization?.billingAddress?.state
          ? {
              value: organization?.billingAddress?.state,
              label: organization?.billingAddress?.state
            }
          : null,
        postalCode: organization?.billingAddress?.postalCode ?? '',
        country: 'US'
      }
    },
    validationSchema: companyProfileSchema
  });

  const onBillingAddressUpdate = () => {
    onCompanyUpdate({
      billingAddress: {
        ...formik.values.billingAddress,
        state: formik.values.billingAddress.state?.label
      }
    });
  };

  function NameEditInput() {
    return (
      <Input
        value={formik.values.name}
        inputProps={{
          onChange: e => formik.setFieldValue('name', e.target.value),
          maxLength: MAX_COMPANY_NAME_LENGTH
        }}
        formItem={{
          placeholder: t('businessName'),
          label: t('businessName'),
          name: 'name',
          validations: {
            required: {
              required: true,
              message: t('inputEmptyWarning', { name: t('businessName') })
            }
          }
        }}
      />
    );
  }
  function CityEditInput() {
    return (
      <Input
        value={formik.values.billingAddress.city}
        inputProps={{
          onChange: e => formik.setFieldValue('billingAddress.city', e.target.value),
          maxLength: MAX_CITY_NAME_LENGTH
        }}
        formItem={{
          placeholder: t('city'),
          label: t('city'),
          name: 'city',
          validations: {
            required: {
              required: true,
              message: t('inputEmptyWarning', { name: t('city') })
            }
          }
        }}
      />
    );
  }

  function CompanySizeEditInput() {
    return (
      <Select
        placeholder={formik.values.companySize?.label || t('companySize')}
        onChange={data => {
          formik.setFieldValue('companySize', data);
        }}
        value={formik.values.companySize?.label ? formik.values.companySize : null}
        options={[
          { value: '1-25', label: t('lessThan25') },
          { value: '25-50', label: '25-50' },
          { value: '50-100', label: '50-100' },
          { value: '100-200', label: '100-200' },
          { value: '200-1000', label: '200-1000' },
          { value: '1,000', label: '10,0000' },
          { value: '10,000+', label: '10,000+' }
        ]}
        size="md"
        simple
        styles={{
          control: () => ({
            marginBottom: 12
          })
        }}
      />
    );
  }

  function DepartmentEditInput() {
    return (
      <Select
        placeholder={t('department')}
        onChange={data => {
          formik.setFieldValue('department', data);
        }}
        value={formik.values.department?.label ? formik.values.department : null}
        options={[
          { value: t('sales'), label: t('sales') },
          { value: t('procurement'), label: t('procurement') },
          { value: t('marketing'), label: t('marketing') },
          { value: t('humanResources'), label: t('humanResources') },
          { value: t('bizDev'), label: t('bizDev') },
          { value: t('clientSupport'), label: t('clientSupport') },
          { value: t('it'), label: t('it') }
        ]}
        size="md"
        styles={{
          control: () => ({
            marginBottom: 12
          })
        }}
      />
    );
  }

  function BillingAddressEditInput() {
    return (
      <Input
        value={formik.values.billingAddress.addressLine1}
        inputProps={{
          onChange: e => formik.setFieldValue('billingAddress.addressLine1', e.target.value),
          maxLength: MAX_ADDRESS_LENGTH
        }}
        formItem={{
          placeholder: t('addressLine1'),
          label: t('addressLine1'),
          name: 'billingAddress.addressLine1',
          validations: {
            required: {
              required: true,
              message: t('inputEmptyWarning', { name: t('addressLine1') })
            }
          }
        }}
      />
    );
  }

  function StateEditInput() {
    return (
      <Select
        placeholder={formik.values.billingAddress.state?.label || t('state')}
        onChange={state => {
          formik.setFieldValue('billingAddress.state', state);
        }}
        value={
          formik.values.billingAddress.state?.label ? formik.values.billingAddress.state : null
        }
        options={US_STATE_LIST}
        styles={{
          control: () => ({
            marginBottom: 12
          })
        }}
      />
    );
  }

  function ZipCodeEditInput() {
    return (
      <Input
        value={formik.values.billingAddress.postalCode}
        inputProps={{
          onChange: e => formik.setFieldValue('billingAddress.postalCode', e.target.value),
          maxLength: MAX_ZIP_CODE_LENGTH
        }}
        formItem={{
          placeholder: t('postalCode'),
          label: t('postalCode'),
          name: 'billingAddress.postalCode',
          validations: {
            required: {
              required: true,
              message: t('inputEmptyWarning', { name: t('postalCode') })
            }
          }
        }}
      />
    );
  }

  return (
    <Card>
      <S.FormContent>
        <Typography variant="body1Bold">{t('companyInformation')}</Typography>

        <DetailItem
          title={`${t('businessName')}:`}
          withBorder
          bodyStyle={{
            display: 'flex',
            alignItems: 'center'
          }}
          editable={isOrganizationOwner}
          closeWithoutSave={() => {
            formik.setFieldValue('name', organization.name);
          }}
          saveDetail={() => {
            onCompanyUpdate({ name: formik.values.name });
          }}
          editInput={NameEditInput}
        >
          {formik.values.name}
        </DetailItem>

        <DetailItem
          title={`${t('businessAddress')}:`}
          withBorder
          bodyStyle={{
            display: 'flex',
            alignItems: 'center'
          }}
          editable={isOrganizationOwner}
          closeWithoutSave={() => {
            formik.setFieldValue(
              'billingAddress.addressLine1',
              organization?.billingAddress?.addressLine1
            );
          }}
          saveDetail={() => {
            onBillingAddressUpdate();
          }}
          editInput={BillingAddressEditInput}
        >
          {formik.values.billingAddress.addressLine1}
        </DetailItem>

        <DetailItem
          title={`${t('country')}:`}
          withBorder
          bodyStyle={{
            display: 'flex',
            alignItems: 'center'
          }}
        >
          US
        </DetailItem>

        <DetailItem
          title={`${t('city')}:`}
          withBorder
          bodyStyle={{
            display: 'flex',
            alignItems: 'center'
          }}
          editable={isOrganizationOwner}
          closeWithoutSave={() => {
            formik.setFieldValue('billingAddress.city', organization?.billingAddress?.city);
          }}
          saveDetail={() => {
            onBillingAddressUpdate();
          }}
          editInput={CityEditInput}
        >
          {formik.values.billingAddress.city}
        </DetailItem>

        <DetailItem
          title={`${t('state')}/${t('province')}:`}
          withBorder
          editable={isOrganizationOwner}
          closeWithoutSave={() => {
            formik.setFieldValue(
              'billingAddress.state',
              typeof organization?.billingAddress?.state === 'string'
                ? {
                    value: organization?.billingAddress?.state,
                    label: organization?.billingAddress?.state
                  }
                : null
            );
          }}
          saveDetail={() => onBillingAddressUpdate()}
          editInput={StateEditInput}
        >
          {formik.values.billingAddress.state?.label}
        </DetailItem>

        <DetailItem
          title={`${t('ZIP')}/${t('postalCode')}:`}
          withBorder
          editable={isOrganizationOwner}
          closeWithoutSave={() => {
            formik.setFieldValue(
              'billingAddress.postalCode',
              organization?.billingAddress?.postalCode
            );
          }}
          saveDetail={() => onBillingAddressUpdate()}
          editInput={ZipCodeEditInput}
        >
          {formik.values.billingAddress.postalCode}
        </DetailItem>

        <DetailItem
          title={`${t('companySize')}:`}
          withBorder
          bodyStyle={{
            display: 'flex',
            alignItems: 'center'
          }}
          editable={isOrganizationOwner}
          closeWithoutSave={() => {
            formik.setFieldValue('companySize', {
              value: organization.companySize,
              label:
                organization.companySize === '1-25' ? t('lessThan25') : organization.companySize
            });
          }}
          saveDetail={() => {
            onCompanyUpdate({ companySize: formik.values.companySize?.value });
          }}
          editInput={CompanySizeEditInput}
        >
          {formik.values.companySize?.label}
        </DetailItem>

        <DetailItem
          title={`${t('department')}:`}
          bodyStyle={{
            display: 'flex',
            alignItems: 'center'
          }}
          editable={isOrganizationOwner}
          closeWithoutSave={() => {
            formik.setFieldValue('department', {
              value: organization.department,
              label: organization.department
            });
          }}
          saveDetail={() =>
            !!formik.values.department &&
            onCompanyUpdate({ department: formik.values.department?.value })
          }
          editInput={DepartmentEditInput}
        >
          {formik.values.department?.label}
        </DetailItem>
      </S.FormContent>
    </Card>
  );
}

CompanyProfileForm.propTypes = {
  organization: PropTypes.object.isRequired
};

export { CompanyProfileForm };
